import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import BookingContext from './BookingContext';
import useCar from './useCar';

import usePaymentSettings from 'components/context/PaymentSettings/usePaymentSettings';
import { PAYMENT_METHODS } from './helpers';

import { bookAndPaidLater, bookAndNowLater } from 'store/actions';
import { APP_PATHS } from 'components/routes/paths';
import { pickupTimeNow } from './CarProvider';
import { carDetailSelectors } from 'store/selectors';

function createParamsForBooking({
	pickupLocation,
	dropOffCity,
	id,
	pickupDate,
	dropDate,
	carDriver,
	unlimitedKm,
	insurance,
	pickupTime,
	cancelPreviousBooking = false,
	rewardOptions
}) {
	const payload = {
		address: pickupLocation ? pickupLocation.address : '',
		lat: pickupLocation ? pickupLocation.lat : '',
		lng: pickupLocation ? pickupLocation.lng : '',
		drop_city_uuid: dropOffCity ? dropOffCity.uuid : '',
		car: id,
		pickup_date: moment(pickupDate).format('YYYY-MM-DD'),
		drop_date: moment(dropDate).format('YYYY-MM-DD'),
		customer_location_lat: null,
		customer_location_lng: null,
		car_delivery: 0,
		car_driver: carDriver ? 1 : 0,
		unlimited_km: unlimitedKm ? 1 : 0,
		insurance: insurance ? 1 : 0,
		cancel_previous_booking: cancelPreviousBooking ? 1 : 0,
		delivery_lat: null,
		delivery_lng: null,
		delivery_address: null,
		rewardOptions,
		pickup_time:
			pickupTime === pickupTimeNow ? moment().format('HH:mm') : pickupTime
	};

	return payload;
}

const BookingProvider = ({ children }) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const rewardOptions = useSelector(carDetailSelectors.getRewardOptions);

	const { settings } = usePaymentSettings();

	const {
		carDetails,
		id,
		pickupLocation,
		pickupDate,
		dropDate,
		insurance,
		unlimitedKm,
		carDriver,
		pickupTime,
		dropOffCity
	} = useCar();

	const { isError, error } = useSelector((state) => state.booking);

	const [isPaymentMethodsVisible, setIsPaymentMethodsVisible] = useState(
		false
	);

	const [paymentMethod, setPaymentMethod] = useState();

	const { cashPaymentsEnabled, cardPaymentsEnabled } = useMemo(() => {
		if (!carDetails || !settings) {
			return {
				cashPaymentsEnabled: false,
				cardPaymentsEnabled: false
			};
		}

		const onlinePaymentsEnabled =
			carDetails?.paymentMethods?.online_payment?.is_active || false;
		const cashPaymentsEnabled =
			carDetails?.paymentMethods?.cash?.is_active || false;

		const cardPaymentsEnabled =
			(onlinePaymentsEnabled && settings?.payment_methods?.card) || false;

		return {
			cashPaymentsEnabled,
			cardPaymentsEnabled
		};
	}, [settings, carDetails]);

	const togglePaymentMethodsVisible = () => {
		setIsPaymentMethodsVisible(!isPaymentMethodsVisible);
	};

	const handleChangePaymentMethod = (ev) => setPaymentMethod(ev.target.value);

	const handleBook = (cancelPreviousBooking) => {
		switch (paymentMethod) {
			case PAYMENT_METHODS.CASH:
				paidLater(cancelPreviousBooking);
				break;
			case PAYMENT_METHODS.CARD:
				paidNow(cancelPreviousBooking);
				break;
			default:
				break;
		}
	};

	const paidLater = (cancelPreviousBooking) => {
		const params = createParamsForBooking({
			pickupLocation,
			dropOffCity,
			id,
			pickupDate,
			dropDate,
			carDriver,
			unlimitedKm,
			insurance,
			pickupTime,
			cancelPreviousBooking,
			rewardOptions
		});

		bookAndPaidLater(
			params,
			() => {},
			(booking) => {
				history.push(
					APP_PATHS.BOOKING_CONFIRMATION.replace(
						':bookingId',
						booking.uuid
					)
				);
			}
		)(dispatch);
	};

	const paidNow = (cancelPreviousBooking) => {
		const params = createParamsForBooking({
			pickupLocation,
			dropOffCity,
			id,
			pickupDate,
			dropDate,
			carDriver,
			unlimitedKm,
			insurance,
			pickupTime,
			cancelPreviousBooking,
			rewardOptions
		});

		bookAndNowLater(
			params,
			() => {},
			(booking) => {
				history.push(
					APP_PATHS.PAYMENT.replace(':bookingId', booking.id).replace(
						':carId',
						id
					)
				);
			}
		)(dispatch);
	};

	useEffect(() => {
		if (paymentMethod) {
			return;
		}

		// Set initial payment method
		if (cardPaymentsEnabled) return setPaymentMethod(PAYMENT_METHODS.CARD);
		if (cashPaymentsEnabled) return setPaymentMethod(PAYMENT_METHODS.CASH);

		// setPaymentMethod('');
		// eslint-disable-next-line
	}, [cashPaymentsEnabled, cardPaymentsEnabled]);

	return (
		<BookingContext.Provider
			value={{
				isError,
				error,
				cashPaymentsEnabled,
				cardPaymentsEnabled,
				isPaymentMethodsVisible,
				togglePaymentMethodsVisible,
				paymentMethod,
				handleChangePaymentMethod,
				handleBook
			}}
		>
			{children}
		</BookingContext.Provider>
	);
};

BookingProvider.propTypes = {
	children: PropTypes.node
};

export default BookingProvider;
